import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  Button,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MovieIcon from '@mui/icons-material/Movie';
import SettingsIcon from '@mui/icons-material/Settings';
import ScienceIcon from '@mui/icons-material/Science';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import RestoreIcon from '@mui/icons-material/Restore';
import MenuIcon from '@mui/icons-material/Menu';
import { getSavedLightState, restoreLightState } from '../services/api';

const drawerWidth = 240;

const menuItems = [
  { text: 'Movies', icon: <MovieIcon />, path: '/app' },
  { text: 'Test', icon: <ScienceIcon />, path: '/app/test' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/app/settings' },
];

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hasSavedState, setHasSavedState] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Check for saved state on mount and when themes are applied
  const checkSavedState = useCallback(async () => {
    try {
      const response = await getSavedLightState();
      setHasSavedState(response.data.hasSavedState);
      // currentTheme is now an object with { name, colors } or null
      setCurrentTheme(response.data.state?.currentTheme || null);
    } catch (err) {
      console.error('Failed to check saved state:', err);
    }
  }, []);

  useEffect(() => {
    checkSavedState();

    // Listen for theme applied events to refresh state
    const handleThemeApplied = () => {
      checkSavedState();
    };
    window.addEventListener('themeApplied', handleThemeApplied);

    return () => {
      window.removeEventListener('themeApplied', handleThemeApplied);
    };
  }, [checkSavedState]);

  const handleRestore = async () => {
    try {
      setRestoring(true);
      await restoreLightState();
      setHasSavedState(false); // Restore clears the saved state
      setCurrentTheme(null); // Clear the current theme
      setSnackbar({ open: true, message: 'Lights restored to initial state!', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to restore lights',
        severity: 'error'
      });
    } finally {
      setRestoring(false);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawerContent = (
    <>
      <Toolbar />
      <Box sx={{ overflow: 'auto' }}>
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <TheaterComedyIcon sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }} />
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            Hue Movie Themes
          </Typography>
          {currentTheme && (
            <Box
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                position: 'relative',
                padding: '6px 14px',
                borderRadius: '6px',
                background: currentTheme.colors?.length >= 2
                  ? `linear-gradient(90deg, ${currentTheme.colors.join(', ')})`
                  : currentTheme.colors?.[0] || 'rgba(255,255,255,0.2)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: '2px',
                  borderRadius: '4px',
                  background: 'rgba(30, 30, 40, 0.95)',
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  fontWeight: 500,
                  letterSpacing: '0.5px',
                  textShadow: currentTheme.colors?.[0]
                    ? `0 0 10px ${currentTheme.colors[0]}40`
                    : 'none',
                }}
              >
                {currentTheme.name}
              </Typography>
            </Box>
          )}
          <Tooltip title={hasSavedState ? "Restore lights to initial state" : "No saved state - apply a movie theme first"}>
            <span>
              <Button
                color="inherit"
                variant="outlined"
                startIcon={restoring ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
                onClick={handleRestore}
                disabled={!hasSavedState || restoring}
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                  // Hide text on very small screens, show icon only
                  '& .MuiButton-startIcon': {
                    mr: { xs: 0, sm: 1 }
                  },
                  minWidth: { xs: 'auto', sm: 'auto' },
                  px: { xs: 1, sm: 2 },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Restore Lights
                </Box>
              </Button>
            </span>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` }
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
          {children}
        </Container>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Layout;
